import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ThemeToggle from './ThemeToggle';

const navLinks = [
  { label: 'Acasă', href: '#hero' },
  { label: 'Despre noi', href: '#despre' },
  { label: 'Servicii', href: '#servicii' },
  { label: 'Portofoliu', href: '#portofoliu' },
  { label: 'De ce noi', href: '#avantaje' },
  { label: 'Contact', href: '#contact' },
];

export default function Navbar({ dark, setDark, bannerVisible = false, bannerHeight = 44 }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClick = (href) => {
    setMobileOpen(false);
    setTimeout(() => {
      const el = document.querySelector(href);
      if (el) {
        const navHeight = 80;
        const top = el.getBoundingClientRect().top + window.scrollY - navHeight;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }, 350);
  };

  return (
    <nav
      data-testid="navbar"
      style={{ top: bannerVisible ? bannerHeight : 0, transition: 'top 0.4s ease' }}
      className={`fixed left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? 'bg-white/85 dark:bg-[#1A1918]/90 backdrop-blur-xl shadow-sm border-b border-[#E3DACB]/50 dark:border-[#3D3A37]/50'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-16 lg:h-20">
        <a
          href="#hero"
          data-testid="navbar-logo"
          onClick={(e) => { e.preventDefault(); handleClick('#hero'); }}
          className="font-['Outfit'] text-xl lg:text-2xl font-semibold tracking-tight text-[#2C2A29] dark:text-[#F0EDE8]"
        >
          Mobila ta <span className="text-[#A67B5B]">Mob</span>
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.href.slice(1)}`}
              onClick={(e) => { e.preventDefault(); handleClick(link.href); }}
              className="text-sm font-medium text-[#6B6561] dark:text-[#9B9590] hover:text-[#A67B5B] transition-colors duration-300"
            >
              {link.label}
            </a>
          ))}
          <ThemeToggle dark={dark} setDark={setDark} />
          <a
            href="#contact"
            data-testid="nav-cta-button"
            onClick={(e) => { e.preventDefault(); handleClick('#contact'); }}
            className="px-5 py-2.5 bg-[#A67B5B] text-white text-sm font-medium rounded-full hover:bg-[#8B654A] transition-colors duration-300"
          >
            Cere ofertă
          </a>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <ThemeToggle dark={dark} setDark={setDark} />
          <button
            data-testid="mobile-menu-toggle"
            className="p-2 text-[#2C2A29] dark:text-[#F0EDE8]"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white/95 dark:bg-[#1A1918]/95 backdrop-blur-xl border-b border-[#E3DACB] dark:border-[#3D3A37]"
          >
            <div className="px-6 py-4 space-y-3">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  data-testid={`mobile-nav-${link.href.slice(1)}`}
                  onClick={(e) => { e.preventDefault(); handleClick(link.href); }}
                  className="block text-base font-medium text-[#2C2A29] dark:text-[#F0EDE8] hover:text-[#A67B5B] py-2 transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="#contact"
                data-testid="mobile-cta-button"
                onClick={(e) => { e.preventDefault(); handleClick('#contact'); }}
                className="block text-center px-5 py-3 bg-[#A67B5B] text-white text-sm font-medium rounded-full hover:bg-[#8B654A] transition-colors mt-2"
              >
                Cere ofertă
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
